import { PROVIDER_CATEGORY_MAPPING, PROVIDERS } from "../config/constants";

/**
 * Validates if a provider-category combination is valid
 * @param {string} provider - The selected provider
 * @param {string} category - The selected category
 * @returns {{isValid: boolean, errorMessage: string|null}} Validation result with error message if invalid
 */
export const validateProviderCategory = (provider, category) => {
  // Empty values are handled by form validation, not here
  if (!provider || !category) {
    return { isValid: true, errorMessage: null };
  }

  // "Custom" category allows "Custom" provider
  if (category === "Custom") {
    return { isValid: true, errorMessage: null };
  }

  // Check if provider exists in mapping
  if (!PROVIDER_CATEGORY_MAPPING[provider]) {
    return {
      isValid: false,
      errorMessage: `Unknown provider: ${provider}. Please select a valid provider.`,
    };
  }

  // Get valid categories for this provider
  const validCategories = PROVIDER_CATEGORY_MAPPING[provider];

  // Check if category is valid for this provider
  if (!validCategories.includes(category)) {
    const categoryList =
      validCategories.length === 1
        ? validCategories[0]
        : `${validCategories.slice(0, -1).join(", ")} or ${
            validCategories[validCategories.length - 1]
          }`;

    return {
      isValid: false,
      errorMessage: `${provider} can only be used with ${categoryList} category${
        validCategories.length > 1 ? "ies" : ""
      }. Please select the correct provider and category before importing.`,
    };
  }

  return { isValid: true, errorMessage: null };
};

/**
 * Detects provider name from filename (case-insensitive)
 * @param {string} filename - The filename to analyze
 * @returns {string|null} Detected provider name or null if not found
 */
export const detectProviderFromFilename = (filename) => {
  if (!filename || typeof filename !== "string") {
    return null;
  }

  const normalizedFilename = filename.toLowerCase();

  // Check each provider (case-insensitive)
  for (const provider of PROVIDERS) {
    const providerLower = provider.toLowerCase();
    if (normalizedFilename.includes(providerLower)) {
      return provider;
    }
  }

  return null;
};

/**
 * Validates if the selected provider matches the provider detected in filename
 * @param {string} selectedProvider - The provider selected by user
 * @param {string} filename - The filename to check
 * @returns {{isMatch: boolean, detectedProvider: string|null, errorMessage: string|null}} Validation result
 */
export const validateFileProviderMatch = (selectedProvider, filename) => {
  if (!selectedProvider || !filename) {
    return { isMatch: true, detectedProvider: null, errorMessage: null };
  }

  // For "Custom" provider, skip filename validation
  // This allows "Custom" category with "Custom" provider to work with generic files
  if (selectedProvider === "Custom") {
    return { isMatch: true, detectedProvider: null, errorMessage: null };
  }

  const detectedProvider = detectProviderFromFilename(filename);

  // If no provider detected in filename, allow import (user might have renamed file)
  if (!detectedProvider) {
    return { isMatch: true, detectedProvider: null, errorMessage: null };
  }

  // If detected provider matches selected provider, validation passes
  if (detectedProvider === selectedProvider) {
    return { isMatch: true, detectedProvider, errorMessage: null };
  }

  // Provider mismatch detected
  return {
    isMatch: false,
    detectedProvider,
    errorMessage: `Selected provider is "${selectedProvider}" but file appears to be for "${detectedProvider}". Please select the correct provider and category before importing.`,
  };
};

/**
 * Validates file type (Excel or CSV)
 * @param {File} file - The file to validate
 * @returns {{isValid: boolean, errorMessage: string|null}} Validation result
 */
export const validateFileType = (file) => {
  if (!file) {
    return { isValid: false, errorMessage: "No file selected." };
  }

  const allowedTypes = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
    "application/csv",
  ];

  const hasValidType = allowedTypes.includes(file.type);
  const hasValidExtension = /\.(csv|xlsx|xls)$/i.test(file.name);

  if (!hasValidType && !hasValidExtension) {
    return {
      isValid: false,
      errorMessage: "Please select a valid Excel (.xlsx, .xls) or CSV file.",
    };
  }

  return { isValid: true, errorMessage: null };
};

/**
 * Validates file size
 * @param {File} file - The file to validate
 * @param {number} maxSizeBytes - Maximum file size in bytes
 * @returns {{isValid: boolean, errorMessage: string|null}} Validation result
 */
export const validateFileSize = (file, maxSizeBytes) => {
  if (!file) {
    return { isValid: false, errorMessage: "No file selected." };
  }

  if (file.size === 0) {
    return {
      isValid: false,
      errorMessage: "The selected file is empty. Please choose a valid file.",
    };
  }

  if (file.size > maxSizeBytes) {
    const maxSizeMB = maxSizeBytes / (1024 * 1024);
    return {
      isValid: false,
      errorMessage: `File size is too large. Please choose a file smaller than ${maxSizeMB}MB.`,
    };
  }

  return { isValid: true, errorMessage: null };
};
